import { action, computed, makeObservable, observable } from "mobx";
import { InvoiceDetail } from "src/types";
import { RootStore } from "src/stores/RootStore";
import { GetServiceDetailsByServiceType_uccm_v_customerservices_tiles as customerservice } from "src/api/generated/GetServiceDetailsByServiceType";
import { CUSTOMER_FEATURE } from "src/api/customer";

// "X requires Y": deactivating Y deactivates every service that requires it, transitively.
const FEATURE_PREREQUISITES: Partial<Record<CUSTOMER_FEATURE, CUSTOMER_FEATURE[]>> = {
    [CUSTOMER_FEATURE.KREDI_FLOW_PRO]: [CUSTOMER_FEATURE.KREDI_FLOW],
    [CUSTOMER_FEATURE.KREDI_FLOW_AI_EXTRACT]: [CUSTOMER_FEATURE.KREDI_FLOW_PRO]
};

// Features that cannot be activated while a prerequisite is missing. Micro.Portal rejects them
// with 409 (common/ServicePrerequisites.cs). Kredi Flow Pro is not in here: activating it
// activates Basic along with it.
const HARD_PREREQ_FEATURES = new Set<CUSTOMER_FEATURE>([CUSTOMER_FEATURE.KREDI_FLOW_AI_EXTRACT]);

export class ServicesStore {
    rootStore: RootStore;
    invoiceDetails: InvoiceDetail[] = [];
    refetchServicesData: boolean = false;
    activatedServices: Record<string, boolean> = {};
    customerServices: customerservice[] = [];

    baseServiceId: string | undefined = undefined;
    proServiceId: string | undefined = undefined;

    lastBaseCascade: boolean = false;
    lastDependentCascades: Record<string, boolean> = {};

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            invoiceDetails: observable,
            refetchServicesData: observable,
            customerServices: observable,
            activatedServices: observable,

            setInvoiceDetails: action,
            toggleInvoiceSelected: action,
            toggleAllInvoicesSelected: action,
            setRefetchServicesData: action,

            setActivatedServices: action,
            setCustomerServices: action,
            updateServiceStatus: action,
            restoreServiceState: action,

            isEveryInvoiceSelected: computed,
            isAnyInvoiceSelected: computed,
            invoicesSelected: computed
        });
    }

    setInvoiceDetails = (invoiceDetailsList: InvoiceDetail[]) => {
        this.invoiceDetails = invoiceDetailsList;
    };

    setRefetchServicesData = (refetchServicesData: boolean) => {
        this.refetchServicesData = refetchServicesData;
    };

    toggleInvoiceSelected = (id: string) => {
        this.invoiceDetails = this.invoiceDetails.map((sel) =>
            sel.id === id ? { ...sel, selected: !sel.selected } : { ...sel }
        );
    };

    toggleAllInvoicesSelected = () => {
        this.invoiceDetails = this.invoiceDetails.map((sel) => ({
            ...sel,
            selected: !this.isEveryInvoiceSelected
        }));
    };

    setActivatedServices = (services: Record<string, boolean>) => {
        this.activatedServices = services;
    };

    setCustomerServices = (services: customerservice[]) => {
        this.customerServices = services;

        this.baseServiceId = this.getServiceIdByCustomerFeature(CUSTOMER_FEATURE.KREDI_FLOW);
        this.proServiceId = this.getServiceIdByCustomerFeature(CUSTOMER_FEATURE.KREDI_FLOW_PRO);
    };

    getServiceIdByCustomerFeature = (feature: CUSTOMER_FEATURE): string | undefined => {
        return this.customerServices.find((service) => service.feature === feature)?.serviceid;
    };

    // The customer features are what unlocks a function, and what Micro.Portal checks
    hasCustomerFeature = (feature: number | null | undefined): boolean => {
        if (feature === null || feature === undefined) return false;
        return this.rootStore.navStore?.customer?.features?.some((f) => f.feature === feature) ?? false;
    };

    // Server-side state: the customerservices row is activated, or the customer has the feature
    isServiceActivated = (serviceId: string): boolean => {
        const service = this.customerServices.find((s) => s.serviceid === serviceId);
        return !!service?.activated || this.hasCustomerFeature(service?.feature);
    };

    arePrerequisitesMet = (feature: number | null | undefined): boolean => {
        if (feature === null || feature === undefined) return true;
        if (!HARD_PREREQ_FEATURES.has(feature as CUSTOMER_FEATURE)) return true;

        const prereqs = FEATURE_PREREQUISITES[feature as CUSTOMER_FEATURE] ?? [];
        return prereqs.every((prereqFeature) => this.hasCustomerFeature(prereqFeature));
    };

    // Every service that requires this one, directly or transitively, deepest first
    getDependentsRecursive = (serviceId: string): string[] => {
        const targetFeature = this.customerServices.find((s) => s.serviceid === serviceId)?.feature;
        if (targetFeature === null || targetFeature === undefined) return [];

        const result: string[] = [];
        for (const [feature, prereqs] of Object.entries(FEATURE_PREREQUISITES)) {
            if (!prereqs?.includes(targetFeature as CUSTOMER_FEATURE)) continue;
            const dependentId = this.getServiceIdByCustomerFeature(Number(feature) as CUSTOMER_FEATURE);
            if (!dependentId) continue;
            result.push(...this.getDependentsRecursive(dependentId));
            result.push(dependentId);
        }
        return result;
    };

    // Only active dependents are deactivated, so inactive ones send no deactivation mails
    getActiveDependents = (serviceId: string): string[] => {
        return this.getDependentsRecursive(serviceId).filter((dependentId) => this.isServiceActivated(dependentId));
    };

    updateServiceStatus = (serviceId: string, isActive: boolean) => {
        const next = { ...this.activatedServices, [serviceId]: isActive };

        if (isActive) {
            if (serviceId === this.proServiceId && this.baseServiceId && !next[this.baseServiceId]) {
                next[this.baseServiceId] = true;
                this.lastBaseCascade = true;
            }
        } else {
            const cascaded: Record<string, boolean> = {};
            for (const dependentId of this.getDependentsRecursive(serviceId)) {
                if (next[dependentId]) {
                    cascaded[dependentId] = true;
                    next[dependentId] = false;
                }
            }
            this.lastDependentCascades = cascaded;
        }

        this.activatedServices = next;
    };

    restoreServiceState = (serviceId: string, previousState: boolean) => {
        this.activatedServices = { ...this.activatedServices, [serviceId]: previousState };
    };

    get isEveryInvoiceSelected(): boolean {
        return this.invoiceDetails.every((e) => e.selected === true);
    }

    get isAnyInvoiceSelected(): boolean {
        return this.invoiceDetails.some((e) => e.selected === true);
    }

    get invoicesSelected(): InvoiceDetail[] {
        return this.invoiceDetails.filter((e) => e.selected === true);
    }
}
